import { useCallback, useMemo, useRef, useState } from 'react';
import { getApiBaseUrl } from '../../config/apiBase';
import { adminFetchInit } from '../../config/adminApi';
import { downloadSearchArtifactBundle } from './adminSearchBundle';
import { runNlpRowSearch } from './adminNlpSearchRunner';

const DEFAULT_QUERIES = [
  'Example Person Example City',
  'Example Brand example.com',
  'person@example.com',
  '+15551234567',
  'example.org',
  'Acme Holdings Ltd',
  'Jane Q Public',
  'sample.user@example.org',
  '+442071838750',
  'Contoso Ltd example.net',
].join('\n');

const MODE_BADGE = {
  person: 'border-cyan-400/35 bg-cyan-950/40 text-cyan-100',
  company: 'border-cyan-500/30 bg-cyan-950/40 text-indigo-900',
  email: 'border-violet-400/35 bg-violet-950/40 text-violet-100',
  phone: 'border-emerald-400/35 bg-emerald-950/40 text-emerald-100',
  invalid: 'border-rose-400/35 bg-rose-950/40 text-rose-100',
};

const PILL_META = {
  domain: { emoji: '🌐', label: 'Domain', tone: 'domain' },
  company: { emoji: '🏢', label: 'Company', tone: 'company' },
  location: { emoji: '📍', label: 'Location', tone: 'location' },
  person: { emoji: '👤', label: 'Person', tone: 'person' },
};

function nlpExportTimestamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`
    + `_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
  );
}

function triggerBlobDownload(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function csvEscape(value) {
  const s = String(value ?? '');
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function pillTextByType(row, type) {
  const pills = Array.isArray(row?.pills) ? row.pills : [];
  const fromPills = pills.filter((p) => String(p?.type || '').toLowerCase() === type).map((p) => p.text);
  if (fromPills.length) return fromPills.join('; ');
  if (type === 'domain') return row.domain || '';
  if (type === 'company') return row.company_name || '';
  if (type === 'person') return row.person_focus || (row.detected_mode === 'person' ? row.identity : '') || '';
  if (type === 'location') {
    return Array.isArray(row.location_tags) ? row.location_tags.join('; ') : '';
  }
  return '';
}

function rowsToCsv(rows) {
  const header = [
    'Input Query',
    'Detected Mode',
    'Domain',
    'Company',
    'Location',
    'Person',
    'Parsed Target',
    'Engine Status',
    'Guidance',
  ];
  const lines = [header.join(',')];
  for (const row of rows || []) {
    const engineStatus = [row.engine, row.status].filter(Boolean).join(' / ');
    const guidance = row.guidance || row.resolved_context?.message || '';
    lines.push(
      [
        csvEscape(row.query),
        csvEscape(row.detected_mode),
        csvEscape(pillTextByType(row, 'domain')),
        csvEscape(pillTextByType(row, 'company')),
        csvEscape(pillTextByType(row, 'location')),
        csvEscape(pillTextByType(row, 'person')),
        csvEscape(row.identity || row.stream_name || ''),
        csvEscape(engineStatus),
        csvEscape(guidance),
      ].join(','),
    );
  }
  return `${lines.join('\n')}\n`;
}

function rowKey(row, idx) {
  return `${String(row?.query || '').trim()}::${idx}`;
}

function TagBadge({ children, tone }) {
  const cls =
    tone === 'location'
      ? 'border-amber-400/40 bg-amber-950/40 text-amber-100'
      : tone === 'company'
        ? 'border-cyan-500/30 bg-cyan-950/40 text-indigo-900'
        : tone === 'domain'
          ? 'border-sky-300 bg-sky-50 text-sky-900'
          : tone === 'person'
            ? 'border-cyan-300 bg-cyan-50 text-cyan-900'
            : 'border-slate-300 bg-slate-50 text-slate-800';
  return (
    <span className={`inline-flex max-w-full items-center gap-1 truncate rounded-md border px-1.5 py-0.5 text-[10px] font-bold ${cls}`}>
      {children}
    </span>
  );
}

function HomeStylePills({ row }) {
  let pills = Array.isArray(row?.pills) ? row.pills : [];
  if (!pills.length) {
    const fallback = [];
    if (row.person_focus) fallback.push({ type: 'person', text: row.person_focus });
    if (row.domain) fallback.push({ type: 'domain', text: row.domain });
    for (const loc of row.location_tags || []) fallback.push({ type: 'location', text: loc });
    if (row.company_name) fallback.push({ type: 'company', text: row.company_name });
    if (!fallback.length && row.identity && row.detected_mode === 'person') {
      fallback.push({ type: 'person', text: row.identity });
    }
    pills = fallback;
  }
  if (!pills.length) return <span className="text-slate-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1" data-testid="admin-nlp-home-pills">
      {pills.map((p, idx) => {
        const type = String(p?.type || '').toLowerCase();
        const meta = PILL_META[type] || { emoji: '•', label: type, tone: 'person' };
        return (
          <TagBadge key={`${type}-${p.text}-${idx}`} tone={meta.tone}>
            <span aria-hidden>{meta.emoji}</span>
            <span className="opacity-70">{meta.label}</span>
            {p.text}
          </TagBadge>
        );
      })}
    </div>
  );
}

function Spinner({ className = 'h-3.5 w-3.5' }) {
  return (
    <span
      className={`inline-block animate-spin rounded-full border-2 border-cyan-300/30 border-t-cyan-300 ${className}`}
      aria-hidden
    />
  );
}

/**
 * Admin NLP & Testing Lab — offline batch query classification + live search bundles.
 *
 * @param {{}} _props
 */
export default function AdminNlpLab() {
  const base = getApiBaseUrl();
  const [rawText, setRawText] = useState(DEFAULT_QUERIES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  /** @type {[Record<string, { status: string, error?: string, artifact?: object, resultCount?: number }>, Function]} */
  const [searchState, setSearchState] = useState({});
  const abortMapRef = useRef(new Map());

  const queryCount = useMemo(
    () =>
      String(rawText || '')
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean).length,
    [rawText],
  );

  const runBatch = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const queries = String(rawText || '')
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      const res = await fetch(
        `${base}/admin/nlp-benchmark`,
        adminFetchInit({
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ queries }),
        }),
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.error || data?.detail || `HTTP ${res.status}`);
      }
      setReport(data);
      setSearchState({});
      for (const ctrl of abortMapRef.current.values()) {
        try {
          ctrl.abort();
        } catch {
          /* ignore */
        }
      }
      abortMapRef.current = new Map();
    } catch (exc) {
      setError(String(exc?.message || exc));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [base, rawText]);

  const rows = Array.isArray(report?.results) ? report.results : [];

  const kpis = useMemo(() => {
    const fromReport = report?.kpis;
    if (fromReport && typeof fromReport === 'object') {
      return {
        totalQueries: Number(fromReport.total_queries) || rows.length,
        personCount: Number(fromReport.person_count) || 0,
        companyCount: Number(fromReport.company_count) || 0,
        emailCount: Number(fromReport.email_count) || 0,
        phoneCount: Number(fromReport.phone_count) || 0,
        averageParseTimeMs: Number(fromReport.average_parse_time_ms) || 0,
      };
    }
    const counts = { person: 0, company: 0, email: 0, phone: 0 };
    let parseSum = 0;
    for (const row of rows) {
      const mode = String(row?.detected_mode || '').toLowerCase();
      if (counts[mode] != null) counts[mode] += 1;
      parseSum += Number(row?.parse_time_ms) || 0;
    }
    return {
      totalQueries: rows.length,
      personCount: counts.person,
      companyCount: counts.company,
      emailCount: counts.email,
      phoneCount: counts.phone,
      averageParseTimeMs: rows.length ? Math.round((parseSum / rows.length) * 10) / 10 : 0,
    };
  }, [report, rows]);

  const exportJson = useCallback(() => {
    if (!report || !rows.length) return;
    const stamp = nlpExportTimestamp();
    const blob = new Blob([JSON.stringify(report, null, 2)], {
      type: 'application/json;charset=utf-8',
    });
    triggerBlobDownload(`nlp_benchmark_results_${stamp}.json`, blob);
  }, [report, rows.length]);

  const exportCsv = useCallback(() => {
    if (!rows.length) return;
    const stamp = nlpExportTimestamp();
    const blob = new Blob([rowsToCsv(rows)], {
      type: 'text/csv;charset=utf-8',
    });
    triggerBlobDownload(`nlp_benchmark_results_${stamp}.csv`, blob);
  }, [rows]);

  const handleRunSearch = useCallback(
    async (row, idx) => {
      const key = rowKey(row, idx);
      const mode = String(row?.detected_mode || '').toLowerCase();
      if (mode === 'invalid') return;

      const prev = abortMapRef.current.get(key);
      if (prev) {
        try {
          prev.abort();
        } catch {
          /* ignore */
        }
      }
      const ctrl = new AbortController();
      abortMapRef.current.set(key, ctrl);

      setSearchState((s) => ({
        ...s,
        [key]: { status: 'running', error: undefined, artifact: undefined },
      }));

      try {
        const result = await runNlpRowSearch(row, { baseUrl: base, signal: ctrl.signal });
        setSearchState((s) => ({
          ...s,
          [key]: {
            status: 'ready',
            artifact: {
              target: result.target,
              moduleName: result.moduleName,
              results: result.results,
              logs: result.logs,
              extractedLeads: result.extractedLeads,
            },
            resultCount: Array.isArray(result.results) ? result.results.length : 0,
            logCount: Array.isArray(result.logs) ? result.logs.length : 0,
          },
        }));
      } catch (exc) {
        if (ctrl.signal.aborted) {
          setSearchState((s) => ({
            ...s,
            [key]: { status: 'idle', error: 'Aborted' },
          }));
          return;
        }
        setSearchState((s) => ({
          ...s,
          [key]: {
            status: 'error',
            error: String(exc?.message || exc),
          },
        }));
      } finally {
        abortMapRef.current.delete(key);
      }
    },
    [base],
  );

  const handleDownloadBundle = useCallback(async (row, idx) => {
    const key = rowKey(row, idx);
    const st = searchState[key];
    if (!st?.artifact || st.status !== 'ready') return;
    setSearchState((s) => ({
      ...s,
      [key]: { ...s[key], status: 'bundling' },
    }));
    try {
      await downloadSearchArtifactBundle(st.artifact);
      setSearchState((s) => ({
        ...s,
        [key]: { ...s[key], status: 'ready' },
      }));
    } catch (exc) {
      setSearchState((s) => ({
        ...s,
        [key]: {
          ...s[key],
          status: 'ready',
          error: String(exc?.message || exc),
        },
      }));
    }
  }, [searchState]);

  return (
    <section className="space-y-4" data-testid="admin-nlp-lab">
      <div className="rounded-2xl border border-white/10 bg-slate-900/55 p-5 shadow-sm">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">
              NLP & Testing Lab
            </h2>
            <p className="mt-1 max-w-2xl text-[11px] leading-relaxed text-slate-500">
              Batch offline classification (detect mode + person/company tags + phone/email parse). Use{' '}
              <span className="font-semibold text-slate-300">Run Search</span> per row to consume SERP credits,
              then download a ZIP bundle (debug JSON + complete logs + PDF).
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={exportJson}
              disabled={!rows.length}
              data-testid="admin-nlp-export-json"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-slate-900/55 px-3 py-2 text-xs font-black uppercase tracking-wider text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span aria-hidden>📥</span>
              Export JSON
            </button>
            <button
              type="button"
              onClick={exportCsv}
              disabled={!rows.length}
              data-testid="admin-nlp-export-csv"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-slate-900/55 px-3 py-2 text-xs font-black uppercase tracking-wider text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span aria-hidden>📊</span>
              Export CSV
            </button>
            <button
              type="button"
              onClick={runBatch}
              disabled={loading || queryCount === 0}
              data-testid="admin-nlp-run-batch"
              className="inline-flex items-center gap-2 rounded-xl border border-cyan-600 bg-cyan-600 px-4 py-2 text-xs font-black uppercase tracking-wider text-white shadow-sm transition hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span aria-hidden>🧪</span>
              {loading ? 'Running…' : 'Run Batch Analysis'}
            </button>
          </div>
        </div>

        <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500">
          Queries (one per line) · {queryCount} loaded
          <textarea
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            rows={12}
            spellCheck={false}
            data-testid="admin-nlp-queries"
            className="mt-2 w-full rounded-xl border border-white/10 bg-slate-50 px-3 py-2 font-mono text-[12px] leading-relaxed text-slate-800 outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-500/20"
            placeholder={'sarb mihai vlad arad\narcadia medical iasi\n…'}
          />
        </label>

        {error ? (
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-700">
            {error}
          </p>
        ) : null}

        {report ? (
          <p className="mt-3 text-[11px] text-slate-500">
            {report.count} result(s) · SERP calls: {report.serp_calls ?? 0}
            {report.note ? ` · ${report.note}` : ''}
          </p>
        ) : null}
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/10 bg-slate-900/55 shadow-sm">
        {rows.length > 0 ? (
          <div
            className="grid grid-cols-2 gap-2 border-b border-white/10 bg-slate-50/90 p-3 sm:grid-cols-3 lg:grid-cols-6"
            data-testid="admin-nlp-kpi-strip"
          >
            {[
              { label: 'Total Queries', value: kpis.totalQueries },
              { label: 'Person Count', value: kpis.personCount },
              { label: 'Company Count', value: kpis.companyCount },
              { label: 'Email Count', value: kpis.emailCount },
              { label: 'Phone Count', value: kpis.phoneCount },
              { label: 'Average Parse Time (ms)', value: kpis.averageParseTimeMs },
            ].map((kpi) => (
              <div
                key={kpi.label}
                className="rounded-lg border border-white/10 bg-slate-900/55 px-2.5 py-2 shadow-sm"
              >
                <p className="text-[9px] font-black uppercase tracking-wider text-slate-400">
                  {kpi.label}
                </p>
                <p className="mt-1 text-lg font-black tabular-nums text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>
        ) : null}
        <div className="max-h-[65vh] overflow-y-auto overflow-x-auto">
          <table className="min-w-full text-left text-xs" data-testid="admin-nlp-results-table">
            <thead className="sticky top-0 z-10 border-b border-white/10 bg-slate-50 text-[10px] font-black uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2.5">Input Query</th>
                <th className="px-3 py-2.5">Detected Mode</th>
                <th className="px-3 py-2.5">Home Pills (Domain / Company / Location / Person)</th>
                <th className="px-3 py-2.5">Stream / Identity</th>
                <th className="px-3 py-2.5">Engine / Status</th>
                <th className="px-3 py-2.5">Live Search</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-10 text-center text-slate-400">
                    Run a batch to see classification results.
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => {
                  const mode = String(row.detected_mode || 'person').toLowerCase();
                  const modeClass = MODE_BADGE[mode] || MODE_BADGE.person;
                  const key = rowKey(row, idx);
                  const st = searchState[key] || { status: 'idle' };
                  const canSearch = mode !== 'invalid';
                  const isRunning = st.status === 'running';
                  const isBundling = st.status === 'bundling';
                  const canDownload = st.status === 'ready' && Boolean(st.artifact);

                  return (
                    <tr key={key} className="border-b border-slate-100 align-top hover:bg-slate-50/80">
                      <td className="max-w-[14rem] px-3 py-2.5 font-mono text-[11px] text-slate-700">
                        {row.query}
                      </td>
                      <td className="px-3 py-2.5">
                        <span
                          className={`inline-flex rounded-md border px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${modeClass}`}
                        >
                          {mode}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        {mode === 'invalid' ? (
                          <p className="text-[10px] font-semibold leading-snug text-rose-700">
                            {row.guidance || row.resolved_context?.message || 'Unrecognized'}
                          </p>
                        ) : (
                          <HomeStylePills row={row} />
                        )}
                        {row.phone?.e164 ? (
                          <p className="mt-1 font-mono text-[10px] text-slate-500">
                            {row.phone.line_type || 'PHONE'} · {row.phone.e164}
                            {row.phone.country ? ` · ${row.phone.country}` : ''}
                          </p>
                        ) : null}
                        {row.email?.domain ? (
                          <p className="mt-1 font-mono text-[10px] text-slate-500">
                            {row.email.local_part}@{row.email.domain}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-3 py-2.5">
                        <TagBadge tone="person">
                          {row.stream_name || row.identity || '—'}
                        </TagBadge>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-[10px] text-slate-400">
                        <div>{row.engine || '—'}</div>
                        <div
                          className={
                            row.status === 'ok' ? 'font-bold text-emerald-700' : 'font-bold text-amber-700'
                          }
                        >
                          {row.status || '—'}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex min-w-[9.5rem] flex-col gap-1.5">
                          <button
                            type="button"
                            disabled={!canSearch || isRunning || isBundling}
                            onClick={() => handleRunSearch(row, idx)}
                            data-testid={`admin-nlp-run-search-${idx}`}
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-cyan-500/40 bg-cyan-500/15 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-wider text-cyan-100 transition hover:bg-cyan-500/25 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {isRunning ? <Spinner /> : null}
                            {isRunning ? 'Searching…' : 'Run Search'}
                          </button>
                          <button
                            type="button"
                            disabled={!canDownload || isBundling}
                            onClick={() => handleDownloadBundle(row, idx)}
                            data-testid={`admin-nlp-download-bundle-${idx}`}
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-wider text-emerald-100 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            {isBundling ? <Spinner className="h-3.5 w-3.5 border-emerald-300/30 border-t-emerald-300" /> : null}
                            {isBundling ? 'Packing…' : 'Download Bundle'}
                          </button>
                          {st.status === 'ready' ? (
                            <p className="text-[9px] font-semibold text-emerald-700/90">
                              Ready · {st.resultCount || 0} intel · {st.logCount || 0} logs
                            </p>
                          ) : null}
                          {st.status === 'error' || st.error ? (
                            <p className="text-[9px] font-semibold leading-snug text-rose-600" title={st.error}>
                              {st.error || 'Search failed'}
                            </p>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
